import { token } from '@atlaskit/tokens';
/* eslint-disable @atlaskit/design-system/no-unsafe-design-token-usage */
import { getTokenValue as getTokenValueRaw } from '@atlaskit/tokens/get-token-value';
import { type ThemeState } from '@atlaskit/tokens/theme-config';
import { type vec4 } from 'gl-matrix';

import { Vector3, Vector4 } from '@canvas/vector';

import { telepointerColors } from './collaborator/telepointer.ts';
import {
  allColorMap,
  allDefaultColorMap,
  allStickyColorMap,
} from './colors/color-maps-elements.ts';
import { type ColorName } from './colors/color-names.ts';
import { parseWebColor } from './colors/parser.ts';
import { staticAtlaskitTokenValues } from './colors/static-token-values.ts';
import { type BackgroundColorToken, type TokenName } from './colors/tokens-types.ts';
import { allColorMapTokenNames } from './colors/tokens.ts';
import { fallbackLineColors, fallbackLineColorsDark } from './fallback-colors.ts';
import { type Theme } from './ui/theme.ts';

// Pre-seed only in true server/svgdom environments. Jest/jsdom tests still have
// document + getComputedStyle and should use the browser path, not the static seed.
const shouldSeedStaticTokenValues =
  typeof document === 'undefined' ||
  document.documentElement == null ||
  typeof getComputedStyle === 'undefined';

const tokenCache = new Map<TokenName, string | null>(
  shouldSeedStaticTokenValues
    ? (Object.entries(staticAtlaskitTokenValues) as [TokenName, string][])
    : [],
);

/**
 * A cached version of the `getTokenValue` function from `@atlaskit/tokens`.
 *
 * `getTokenValue` is slow as it has to fetch the value from the DOM every time
 * it's called.
 *
 * On the server (no DOM / svgdom), the cache is pre-seeded with static
 * light-theme hex values from {@link staticAtlaskitTokenValues} so that
 * color resolution produces the same results as the browser.
 */
export const getTokenValue = (tokenName: TokenName, fallback?: string): string => {
  if (tokenCache.has(tokenName)) {
    return tokenCache.get(tokenName) ?? fallback ?? '';
  }

  const tokenValue = getTokenValueRaw(tokenName);
  const hasTokenValue = !!tokenValue;

  tokenCache.set(tokenName, hasTokenValue ? tokenValue : null);

  return hasTokenValue ? tokenValue : (fallback ?? '');
};

/** Returns the light theme value for a token. */
export const getLightThemeTokenValue = (
  tokenName: TokenName,
  fallback?: string,
): string => staticAtlaskitTokenValues[tokenName] ?? fallback ?? '';

let tokenCacheVersion = 0;

/**
 * Derived colour caches use this version to detect token-cache invalidation.
 */
export const getTokenCacheVersion = (): number => tokenCacheVersion;

/**
 * Derived cache invalidation is opt-in while theme-aware import colours roll out.
 */
export const clearTokenCache = ({ invalidateDerivedCaches = false } = {}) => {
  tokenCache.clear();
  if (invalidateDerivedCaches) {
    tokenCacheVersion++;
  }
};

/**
 * Returns the given color name as a Vector3.
 */
export const getColorVec3 = (color: ColorName): Vector3 => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  const tokenValue = getTokenValue(allColorMapTokenNames[color]);
  return tokenValue ? Vector3.fromHexColor(tokenValue) : allColorMap[color];
};

/** Resolves the board surface CSS variable, including background experiment overrides. */
export const getCanvasBackgroundColorVec3 = (): Vector3 => {
  if (typeof document !== 'undefined' && typeof getComputedStyle !== 'undefined') {
    const color = getComputedStyle(document.documentElement)
      .getPropertyValue('--canvas-board-surface')
      .trim();
    const rgb = parseWebColor(color);
    if (rgb) {
      return new Vector3(rgb[0], rgb[1], rgb[2]);
    }
  }
  // Server imports and standalone stories have no board stylesheet.
  return getColorVec3('neutralLight');
};

/**
 * Returns the given color name as a Vector3, or the fallback color if the color could
 * not be found.
 */
export const getColorVec3FromToken = (
  tokenName: TokenName,
  fallbackColor: Vector3,
): Vector3 => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  const tokenValue = getTokenValue(tokenName);

  return tokenValue ? Vector3.fromHexColor(tokenValue) : fallbackColor;
};

/**
 * Returns the given color name as a Vector4. This is suitable for transparent colors.
 *
 * @param tokenName - The token name to get the color value from.
 * @param fallbackColor - The fallback color to use if the token value is not found.
 * @returns The color value as a Vector4.
 */
export const getColorVec4FromToken = (
  tokenName: TokenName,
  fallbackColor: Vector4,
): Vector4 => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  const tokenValue = getTokenValue(tokenName);

  return tokenValue ? Vector4.fromHexColor(tokenValue) : fallbackColor;
};

/**
 * Returns the given color name as a Vector3, at a given opacity value on top of a given
 * background color.
 */
export const getColorVec3OnBackground = (
  color: ColorName | Vector4,
  opacity = 1,
  backgroundColor: ColorName | Vector3 | Vector4 = 'neutralLight',
): Vector3 => {
  const colorVec4 = typeof color === 'string' ? getColorVec4(color) : color;
  const combinedOpacity = (colorVec4.w / 255) * opacity;

  const backgroundVec3 =
    typeof backgroundColor === 'string' ? getColorVec3(backgroundColor) : backgroundColor;

  return new Vector3(
    Math.round(colorVec4.x * combinedOpacity + backgroundVec3.x * (1 - combinedOpacity)),
    Math.round(colorVec4.y * combinedOpacity + backgroundVec3.y * (1 - combinedOpacity)),
    Math.round(colorVec4.z * combinedOpacity + backgroundVec3.z * (1 - combinedOpacity)),
  );
};

/**
 * Returns the given color name as a Vector4. This is suitable for transparent colors.
 */
export const getColorVec4 = (color: ColorName, opacity = 1): Vector4 => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  const tokenValue = getTokenValue(allColorMapTokenNames[color]);

  return tokenValue
    ? Vector4.fromHexColor(tokenValue, opacity)
    : new Vector4(
        allColorMap[color].x,
        allColorMap[color].y,
        allColorMap[color].z,
        Math.round(255 * opacity),
      );
};

/**
 * Returns the given color name as a Vector3, at a given opacity value on top of a given
 * background color.
 */
export const getColorVec4OnBackground = (
  color: ColorName | Vector4,
  backgroundColor: ColorName | Vector3 | Vector4 = 'neutralLight',
  opacity = 1,
): Vector4 => {
  const colorVec3 = getColorVec3OnBackground(color, opacity, backgroundColor);
  return new Vector4(colorVec3.x, colorVec3.y, colorVec3.z, 255);
};

export const getColorCSSValue = (color: ColorName): string => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  return token(allColorMapTokenNames[color], allColorMap[color].toString('rgb'));
};

export const getColorCSSValueFromToken = (
  tokenName: TokenName,
  fallbackColor: Vector3,
): string => {
  // eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
  return token(tokenName, fallbackColor.toString('rgb'));
};

export const getAccentColorCSSValueForSticky = (
  stickyColor: ColorName,
  isPrivateModeEnabled: boolean,
  storybookColor?: ColorName,
): string => {
  let mainColor = stickyColor;
  if (storybookColor) {
    mainColor = storybookColor;
  }
  if (isPrivateModeEnabled) {
    mainColor = 'n200';
  }
  return getColorCSSValueFromToken(
    allStickyColorMap[mainColor].accent as TokenName,
    getGreyedColor(allDefaultColorMap[mainColor]),
  );
};

export const getRoundedRectColorVec4 = (
  name: keyof typeof fallbackLineColors,
  theme: Theme,
): vec4 => {
  // @todo: make this use token values in https://product-fabric.atlassian.net/browse/CNS-15783
  return theme === 'dark' ? fallbackLineColorsDark[name] : fallbackLineColors[name];
};

export const getSystemPreferredColorScheme = (): 'light' | 'dark' =>
  globalThis.matchMedia && globalThis.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';

export const getThemeFromThemeState = (state: Partial<ThemeState>) => {
  if (!state.colorMode) {
    return 'light';
  }
  if (state.colorMode === 'auto') {
    return getSystemPreferredColorScheme();
  }
  return state.colorMode;
};

// Since clientID (from YJS) is uniformly random, we can directly use it with a mod operator
// to select a random collaborator color client-side that's consistent across all clients.
const randomCollaboratorColor = (
  clientId: number,
  colorsToOmit?: Set<BackgroundColorToken>,
) => {
  const colors = colorsToOmit
    ? telepointerColors.filter((color) => !colorsToOmit.has(color.telepointerColorToken))
    : telepointerColors;
  return colors[clientId % colors.length];
};

export const getCollaboratorColors = (
  clientId: number,
  colorsToOmit?: Set<BackgroundColorToken>,
): {
  telepointerColor: Vector3;
  telepointerColorToken: BackgroundColorToken;
  telepointerToken: string;
  svgBackgroundColor: Vector3;
} => {
  const {
    telepointerColorToken,
    telepointerFallbackColor,
    svgBackgroundColorToken,
    svgBackgroundFallbackColor,
  } = randomCollaboratorColor(clientId, colorsToOmit);

  return {
    telepointerColor: getColorVec3FromToken(
      telepointerColorToken,
      telepointerFallbackColor,
    ),
    telepointerColorToken,
    telepointerToken: token(telepointerColorToken),
    svgBackgroundColor: getColorVec3FromToken(
      svgBackgroundColorToken,
      svgBackgroundFallbackColor,
    ),
  };
};

export const getCollaboratorColor = (clientId: number): Vector3 =>
  getCollaboratorColors(clientId).telepointerColor;

export const getCollaboratorTokenColor = (
  clientId: number,
  colorsToOmit?: Set<BackgroundColorToken>,
): string => getCollaboratorColors(clientId, colorsToOmit).telepointerToken;

export const getTelepointerColorTokenForCollaborator = (
  clientId: number,
  colorsToOmit?: Set<BackgroundColorToken>,
) => {
  return randomCollaboratorColor(clientId, colorsToOmit).telepointerColorToken;
};

export const getCollaboratorSVGBackgroundColor = (clientId: number): Vector3 =>
  getCollaboratorColors(clientId).svgBackgroundColor;

export const getCollaboratorStickyColor = (clientId: number): ColorName =>
  randomCollaboratorColor(clientId).colorName;

/**
 * Creates a greyer version of an RGB color by reducing saturation.
 * @param rgb - The RGB color
 * @returns A greyer version of the color as an RGB color
 */
export const getGreyedColor = (rgb: Vector3): Vector3 => {
  // Mix with 30% grey to reduce saturation
  const greyedR = Math.round(rgb.x * 0.7 + 76.5); // 0.7 * original + 0.3 * 255
  const greyedG = Math.round(rgb.y * 0.7 + 76.5);
  const greyedB = Math.round(rgb.z * 0.7 + 76.5);
  return new Vector3(greyedR, greyedG, greyedB);
};
